import React from 'react';
import { Box, ButtonBase, Fade, Typography } from '@mui/material';
import { CloseRounded } from '@mui/icons-material';
import { tokens } from '../../theme/tokens';
import { useShellTranslation } from '../navigation/useShellTranslation';
import { useAppUpdate } from './UpdateContext';

const { ink, accent, signal, surface, line, radius, shadow } = tokens;

/**
 * First notice of an update (normal and soon). Mount it inside a `position:
 * relative` content area: it sits 16px from that area's left and bottom, so it
 * never covers the sidebar's account card. Below page content's dialogs
 * (z-index 1100 < 1300), above the page.
 */
export const UpdateToast: React.FC = () => {
  const { t } = useShellTranslation();
  const { urgency, reason, toastVisible, closeToast, refresh } = useAppUpdate();
  const soon = urgency === 'soon';

  return (
    <Fade in={toastVisible} timeout={{ enter: 220, exit: 150 }} mountOnEnter unmountOnExit>
      <Box
        role="status"
        aria-live="polite"
        sx={{
          position: 'absolute',
          left: 16,
          bottom: 16,
          zIndex: 1100,
          maxWidth: 'min(380px, calc(100% - 32px))',
          display: 'flex',
          alignItems: soon ? 'flex-start' : 'center',
          gap: '12px',
          p: '10px 8px 10px 14px',
          background: surface.card,
          border: `1px solid ${line.default}`,
          borderRadius: `${radius.card}px`,
          boxShadow: shadow.menu,
          '@media (prefers-reduced-motion: no-preference)': {
            animation: 'appUpdateToastIn 220ms cubic-bezier(.2,.8,.2,1)',
          },
          '@keyframes appUpdateToastIn': {
            from: { transform: 'translateY(8px)' },
            to: { transform: 'none' },
          },
        }}
      >
        <Box
          component="span"
          aria-hidden
          sx={{
            flexShrink: 0,
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: soon ? signal.caution.solid : signal.info.solid,
            // Aligns the dot with the first line of the title.
            mt: soon ? '6px' : 0,
          }}
        />
        {soon ? (
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: ink.primary }}>
              {t('shell.update.soon.title')}
            </Typography>
            <Typography
              sx={{ fontSize: '12.5px', lineHeight: 1.45, color: ink.bodyAlt, overflowWrap: 'anywhere' }}
            >
              {reason || t('shell.update.soon.fallbackReason')}
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ flex: '1 1 auto', minWidth: 0, fontSize: '13px', color: ink.body }}>
            {t('shell.update.normal.toast')}
          </Typography>
        )}
        <ButtonBase
          onClick={refresh}
          sx={{
            flexShrink: 0,
            height: 30,
            px: '10px',
            borderRadius: `${radius.small}px`,
            fontSize: '13px',
            fontWeight: 600,
            color: accent.main,
            '&:hover': { background: accent.tint },
          }}
        >
          {t('shell.update.refresh')}
        </ButtonBase>
        <ButtonBase
          onClick={closeToast}
          aria-label={t('shell.update.dismiss')}
          sx={{
            flexShrink: 0,
            width: 28,
            height: 28,
            borderRadius: `${radius.small}px`,
            color: ink.muted,
            '&:hover': { background: surface.tint },
            '& svg': { fontSize: 14 },
          }}
        >
          <CloseRounded />
        </ButtonBase>
      </Box>
    </Fade>
  );
};
